import { IsString, IsOptional, IsBoolean, IsNumber, IsEnum, IsArray, Min, Max, IsUrl, Validate, ValidateIf } from 'class-validator';
import { ConnectorConfig } from '../../../models';
import { IsCredentialFreeDataSourceUrl } from '../../../data-source-url.validator';

export class ConnectorConfigBox extends ConnectorConfig {
	/**
	 * Folder ID in Box - the unique identifier for the folder to search
	 * Use "0" for root folder
	 */
	@IsOptional()
	@IsString()
	target: string;

	/**
	 * Box application client ID
	 */
	@IsOptional()
	@IsString()
	clientId?: string;

	/**
	 * Box application client secret
	 */
	@IsOptional()
	@IsString()
	clientSecret?: string;

	/**
	 * Developer token for testing/development (not for production)
	 */
	@IsOptional()
	@IsString()
	developerToken?: string;

	/**
	 * OAuth access token
	 */
	@IsOptional()
	@IsString()
	accessToken?: string;

	/**
	 * OAuth refresh token
	 */
	@IsOptional()
	@IsString()
	refreshToken?: string;

	/**
	 * Box API base URL (defaults to https://api.box.com/2.0)
	 */
	@IsOptional()
	@IsString()
	@ValidateIf(o => o.baseUrl && o.baseUrl.trim() !== '')
	@IsUrl({ protocols: ['https'], require_protocol: true })
	@Validate(IsCredentialFreeDataSourceUrl)
	baseUrl?: string = 'https://api.box.com/2.0';

	/**
	 * Request timeout in milliseconds
	 */
	@IsOptional()
	@IsNumber()
	@Min(1000)
	@Max(60000)
	timeout?: number = 30000;

	/**
	 * Whether to include files from subfolders
	 */
	@IsOptional()
	@IsBoolean()
	includeSubfolders?: boolean = true;

	/**
	 * Maximum number of items to return per page
	 */
	@IsOptional()
	@IsNumber()
	@Min(1)
	@Max(1000)
	pageSize?: number = 100;

	/**
	 * File extensions to include (empty array = all files)
	 */
	@IsOptional()
	@IsArray()
	@IsString({ each: true })
	allowedFileTypes?: string[] = [];

	/**
	 * Authentication method to use
	 */
	@IsOptional()
	@IsEnum(['oauth', 'jwt', 'developer_token', 'enterprise'])
	authMethod?: string;

	/**
	 * Whether to include shared links in results
	 */
	@IsOptional()
	@IsBoolean()
	includeSharedLinks?: boolean = false;

	/**
	 * JWT Private Key for enterprise authentication
	 */
	@IsOptional()
	@IsString()
	jwtPrivateKey?: string;

	/**
	 * JWT Private Key Passphrase (if the private key is encrypted)
	 */
	@IsOptional()
	@IsString()
	jwtPrivateKeyPassphrase?: string;

	/**
	 * JWT Key ID for enterprise authentication
	 */
	@IsOptional()
	@IsString()
	jwtKeyId?: string;

	/**
	 * Enterprise ID for JWT authentication
	 */
	@IsOptional()
	@IsString()
	enterpriseId?: string;

	/**
	 * Whether to automatically save selected assets to the library
	 */
	@IsOptional()
	@IsBoolean()
	saveToLibrary?: boolean = true;

	// Legacy/ignored properties from other connector types - accepted but not used
	// These are here to prevent validation errors when the frontend sends them
	@IsOptional()
	@IsString()
	apiKey?: string;

	@IsOptional()
	@IsString()
	columnSeparator?: string;

	@IsOptional()
	@IsString()
	environment?: string;
}

// Box API response models
export interface BoxSearchParams {
	folderId?: string;
	query?: string;
	type?: 'file' | 'folder' | 'web_link';
	fileExtensions?: string[];
	limit?: number;
	offset?: number;
	sort?: 'modified_at' | 'name' | 'size' | 'relevance';
	direction?: 'ASC' | 'DESC';
	ancestorFolderIds?: string[];
	contentTypes?: string[];
	includeRecentSharedLinks?: boolean;
	createdAtRange?: string[];
	updatedAtRange?: string[];

	// VYC-specific filters
	mediaType?: string;
	minSize?: number;
	maxSize?: number;
	tags?: string[];
	nameFilter?: string;
	descriptionFilter?: string;
}

export interface BoxFolderResponse {
	entries: BoxItem[];
	limit: number;
	offset: number;
	total_count: number;
	next_marker?: string;
	prev_marker?: string;
}

export interface BoxSearchResponse {
	type: 'search_results_items';
	entries: BoxItem[];
	limit: number;
	offset: number;
	total_count: number;
}

export interface BoxItem {
	id: string;
	type: 'file' | 'folder' | 'web_link';
	name: string;
	size?: number;
	description?: string;
	created_at: string;
	modified_at: string;
	content_created_at?: string;
	content_modified_at?: string;
	created_by: BoxUser;
	modified_by?: BoxUser;
	owned_by?: BoxUser;
	parent?: BoxFolder;
	path_collection?: BoxPathCollection;
	item_status: 'active' | 'trashed' | 'deleted';
	file_version?: BoxFileVersion;
	sha1?: string;
	extension?: string;
	permissions?: BoxPermissions;
	shared_link?: BoxSharedLink;
	representations?: BoxRepresentations;
	tags?: string[];
	etag: string;
	sequence_id: string;
	trashed_at?: string;
	purged_at?: string;
}

export interface BoxUser {
	id: string;
	type: 'user';
	name: string;
	login: string;
}

export interface BoxFolder {
	id: string;
	type: 'folder';
	name: string;
	etag?: string;
	sequence_id?: string;
}

export interface BoxPathCollection {
	total_count: number;
	entries: BoxFolder[];
}

export interface BoxFileVersion {
	id: string;
	type: 'file_version';
	sha1: string;
}

export interface BoxPermissions {
	can_download: boolean;
	can_edit: boolean;
	can_delete: boolean;
	can_rename: boolean;
	can_share: boolean;
	can_preview: boolean;
	can_comment: boolean;
	can_annotate: boolean;
	can_upload: boolean;
	can_invite_collaborator: boolean;
	can_set_share_access: boolean;
	can_view_annotations_all: boolean;
	can_view_annotations_self: boolean;
}

export interface BoxSharedLink {
	url: string;
	download_url?: string;
	vanity_url?: string;
	vanity_name?: string;
	access: 'open' | 'company' | 'collaborators';
	effective_access: 'open' | 'company' | 'collaborators';
	effective_permission: 'can_edit' | 'can_download' | 'can_preview';
	unshared_at?: string;
	is_password_enabled: boolean;
	permissions: {
		can_download: boolean;
		can_edit: boolean;
		can_preview: boolean;
	};
	download_count: number;
	preview_count: number;
}

export interface BoxRepresentations {
	entries: BoxRepresentation[];
}

export interface BoxRepresentation {
	content?: {
		url_template: string;
	};
	info?: {
		url: string;
	};
	properties?: {
		dimensions?: string;
		paged?: string;
		thumb?: string;
	};
	representation: string;
	status: {
		state: 'success' | 'pending' | 'error';
	};
}

export interface BoxDownloadLink {
	url: string;
	accessToken?: string;
	expiresAt?: string;
}

export interface BoxThumbnailResponse {
	url: string;
	width: number;
	height: number;
	extension: 'png' | 'jpg';
}

export interface BoxOAuthTokenResponse {
	access_token: string;
	refresh_token?: string;
	expires_in: number;
	token_type: string;
	restricted_to?: any[];
}

export interface BoxErrorResponse {
	type: 'error';
	status: number;
	code: string;
	message: string;
	context_info?: any;
	request_id?: string;
}
